import { supabase } from '../lib/supabase';

export const promptService = {
  // Get all active prompts with optional category filter
  async getAll(category = null) {
    try {
      let query = supabase?.from('master_prompts')?.select('*')?.eq('status', 'active')?.order('priority', { ascending: false })?.order('created_at', { ascending: false });
      
      if (category) {
        query = query?.eq('category', category);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      return data?.map(prompt => ({
        id: prompt?.id,
        title: prompt?.title,
        category: prompt?.category,
        content: prompt?.content,
        description: prompt?.description,
        version: prompt?.version,
        status: prompt?.status,
        isNorthStar: prompt?.is_north_star,
        priority: prompt?.priority,
        tags: prompt?.tags,
        metadata: prompt?.metadata,
        createdBy: prompt?.created_by,
        createdAt: prompt?.created_at,
        updatedAt: prompt?.updated_at,
        lastReviewedAt: prompt?.last_reviewed_at,
        reviewedBy: prompt?.reviewed_by
      }));
    } catch (error) {
      console.error('Error fetching prompts:', error);
      throw error;
    }
  },

  // Get north star principles only
  async getNorthStarPrinciples() {
    try {
      const { data, error } = await supabase?.from('master_prompts')?.select('*')?.eq('is_north_star', true)?.eq('status', 'active')?.order('priority', { ascending: false });
      
      if (error) throw error;
      
      return data?.map(prompt => ({
        id: prompt?.id,
        title: prompt?.title,
        content: prompt?.content,
        priority: prompt?.priority,
        tags: prompt?.tags
      }));
    } catch (error) {
      console.error('Error fetching north star principles:', error);
      throw error;
    }
  },

  // Get prompt by ID with version history
  async getById(id) {
    try {
      const { data: prompt, error: promptError } = await supabase?.from('master_prompts')?.select('*')?.eq('id', id)?.single();
      
      if (promptError) throw promptError;
      
      const { data: versions, error: versionsError } = await supabase?.from('prompt_versions')?.select('*')?.eq('prompt_id', id)?.order('version_number', { ascending: false });
      
      if (versionsError) throw versionsError;
      
      return {
        ...prompt,
        isNorthStar: prompt?.is_north_star,
        createdBy: prompt?.created_by,
        createdAt: prompt?.created_at,
        updatedAt: prompt?.updated_at,
        lastReviewedAt: prompt?.last_reviewed_at,
        reviewedBy: prompt?.reviewed_by,
        versions: versions?.map(v => ({
          id: v?.id,
          versionNumber: v?.version_number,
          content: v?.content,
          changesSummary: v?.changes_summary,
          createdBy: v?.created_by,
          createdAt: v?.created_at
        }))
      };
    } catch (error) {
      console.error('Error fetching prompt by ID:', error);
      throw error;
    }
  },

  // Create new prompt
  async create(promptData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { data, error } = await supabase?.from('master_prompts')?.insert({
          title: promptData?.title,
          category: promptData?.category,
          content: promptData?.content,
          description: promptData?.description,
          status: promptData?.status || 'active',
          is_north_star: promptData?.isNorthStar || false,
          priority: promptData?.priority || 0,
          tags: promptData?.tags || [],
          metadata: promptData?.metadata || {},
          created_by: user?.id
        })?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        title: data?.title,
        category: data?.category,
        content: data?.content,
        status: data?.status,
        isNorthStar: data?.is_north_star,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error creating prompt:', error);
      throw error;
    }
  },

  // Update existing prompt
  async update(id, promptData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { data, error } = await supabase?.from('master_prompts')?.update({
          title: promptData?.title,
          category: promptData?.category,
          content: promptData?.content,
          description: promptData?.description,
          status: promptData?.status,
          is_north_star: promptData?.isNorthStar,
          priority: promptData?.priority,
          tags: promptData?.tags,
          metadata: promptData?.metadata
        })?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        title: data?.title,
        content: data?.content,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating prompt:', error);
      throw error;
    }
  },

  // Delete prompt
  async delete(id) {
    try {
      const { error } = await supabase?.from('master_prompts')?.delete()?.eq('id', id);
      
      if (error) throw error;
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting prompt:', error);
      throw error;
    }
  },

  // Get prompt categories
  async getCategories() {
    return [
      { value: 'system_principles', label: 'System Principles' },
      { value: 'validation_rules', label: 'Validation Rules' },
      { value: 'interaction_guidelines', label: 'Interaction Guidelines' },
      { value: 'north_star_principles', label: 'North Star Principles' },
      { value: 'error_handling', label: 'Error Handling' },
      { value: 'security_policies', label: 'Security Policies' }
    ];
  },

  // Initialize persistent navigation requirement
  async initializePersistentNavigation() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;

      // Check if entry already exists
      const { data: existing } = await supabase
        ?.from('master_prompts')
        ?.select('id')
        ?.eq('title', 'Persistent Left Menu Navigation')
        ?.single();

      if (existing) return existing;

      // Create the entry
      const { data, error } = await supabase?.from('master_prompts')?.insert({
        title: 'Persistent Left Menu Navigation',
        category: 'system_principles',
        content: 'The left menu panel (Sidebar) MUST be visible on all authenticated pages and all future pages. This is implemented through a MainLayout component that wraps all authenticated routes in Routes.jsx. The Sidebar contains hierarchical navigation with collapsible parent groups (Business Admin, Super Admin, Core CRM) and role-based visibility controls. Individual pages should NOT import or render Sidebar/Header components directly - this is handled by the MainLayout wrapper.',
        description: 'Architectural requirement for consistent navigation across the entire application',
        status: 'active',
        is_north_star: true,
        priority: 100,
        tags: ['navigation', 'architecture', 'layout', 'sidebar', 'persistent-ui'],
        metadata: {
          implementation: 'MainLayout component in src/components/layouts/MainLayout.jsx',
          applies_to: 'all_authenticated_pages',
          date_established: new Date()?.toISOString()
        },
        created_by: user?.id
      })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error initializing persistent navigation requirement:', error);
      throw error;
    }
  }
};